/** P420 -- plan tests and the lessons register, client side. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type { LessonLearned, PlanTest } from "shared/api/types";

export const continuityTestKeys = {
  lessons: ["twin", "lessons"] as const,
  tests: (planId: string) => ["twin", "plan-tests", planId] as const,
};

export function useLessons(): UseQueryResult<LessonLearned[], ApiError> {
  return useQuery({
    queryKey: continuityTestKeys.lessons,
    queryFn: () => api.get<LessonLearned[]>("/v1/twin/lessons"),
  });
}

export function usePlanTests(
  planId: string | null,
): UseQueryResult<PlanTest[], ApiError> {
  return useQuery({
    queryKey: continuityTestKeys.tests(planId ?? "_none"),
    queryFn: () => api.get<PlanTest[]>(`/v1/twin/continuity-plans/${planId}/tests`),
    enabled: Boolean(planId),
  });
}

export function useMoveLesson(): UseMutationResult<
  LessonLearned,
  ApiError,
  { id: string; status: string; declined_reason?: string; plan_id?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) =>
      api.patch<LessonLearned>(`/v1/admin/twin/lessons/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: continuityTestKeys.lessons });
    },
  });
}

export function useRecordPlanTest(
  planId: string,
): UseMutationResult<
  PlanTest,
  ApiError,
  { method: string; outcome: string; notes?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.post<PlanTest>(`/v1/admin/twin/continuity-plans/${planId}/tests`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: continuityTestKeys.tests(planId) });
      // The plan list carries `next_test_due`, so it is stale too.
      void qc.invalidateQueries({ queryKey: ["twin", "continuity-plans"] });
    },
  });
}

/** Round 14 D4 -- raise a lesson from a plan test (or another source). */
export function useRaiseLesson(): UseMutationResult<
  LessonLearned,
  ApiError,
  {
    source_kind: string;
    source_id: string;
    lesson: string;
    recommended_action?: string | null;
  }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<LessonLearned>("/v1/admin/twin/lessons", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: continuityTestKeys.lessons });
    },
  });
}
